import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Select } from '@/components/ui/Select';
import { CATEGORY_LABEL } from '@/components/pond/page/format';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import type {
  FarmProductCreate,
  FarmProductResponse,
  FarmProductUpdate,
} from '@/types/generated/openapi';

type Category = FarmProductResponse['category'];
type Unit = FarmProductResponse['unit'];

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABEL) as Category[]).map((value) => ({
  value,
  label: CATEGORY_LABEL[value],
}));

const UNIT_OPTIONS: Array<{ value: Unit; label: string }> = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'L', label: 'L' },
  { value: 'mL', label: 'mL' },
  { value: 'pcs', label: 'pcs' },
];

interface FormState {
  name: string;
  category: Category | '';
  unit: Unit | '';
}

const EMPTY_FORM: FormState = { name: '', category: '', unit: '' };

interface ProductDialogProps {
  farmId: string;
  product: FarmProductResponse | null;
  onClose: () => void;
}

function ProductDialog({ farmId, product, onClose }: ProductDialogProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(
    product ? { name: product.name, category: product.category, unit: product.unit } : EMPTY_FORM
  );
  const unitLocked = !!product && (product.use_count ?? 0) > 0;
  const [hint, setHint] = useState('');

  // No retry: a retried create after a lost response would 409 against its own row.
  const save = useMutation({
    retry: false,
    mutationFn: (body: FarmProductCreate | FarmProductUpdate) =>
      product
        ? api.patch(`/api/v1/farms/${farmId}/products/${product.id}`, body)
        : api.post(`/api/v1/farms/${farmId}/products`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) });
      // The server copies a renamed/recategorised product onto its logged applications.
      queryClient.invalidateQueries({ queryKey: ['applications', farmId] });
      onClose();
    },
  });

  const name = form.name.trim();
  const { category, unit } = form;
  const submit = () => {
    if (!name) return setHint('Enter a product name');
    if (!category) return setHint('Choose a category');
    if (!unit) return setHint('Choose a unit');
    setHint('');
    if (!product) return save.mutate({ name, category, unit });
    // Only changed fields: any name/category in the PATCH rewrites every linked application.
    const changes: FarmProductUpdate = {};
    if (name !== product.name) changes.name = name;
    if (category !== product.category) changes.category = category;
    if (unit !== product.unit) changes.unit = unit;
    if (Object.keys(changes).length === 0) return onClose();
    save.mutate(changes);
  };

  return (
    <Dialog open onClose={onClose} title={product ? 'Edit product' : 'Add product'}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          label="Name"
          value={form.name}
          maxLength={100}
          autoFocus
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Select
          label="Category"
          placeholder="Choose a category"
          value={form.category}
          options={CATEGORY_OPTIONS}
          onChange={(e) => setForm({ ...form, category: e.target.value as Category })}
        />
        <Select
          label="Unit"
          placeholder="Choose a unit"
          value={form.unit}
          options={UNIT_OPTIONS}
          disabled={unitLocked}
          description={
            unitLocked
              ? "Can't change after the product has been used. Add a new product for a different unit."
              : undefined
          }
          onChange={(e) => setForm({ ...form, unit: e.target.value as Unit })}
        />
        {hint && <p className="text-sm font-medium text-status-critical">{hint}</p>}
        {save.error && <p className="text-sm font-medium text-status-critical">{save.error.message}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            {product ? 'Save' : 'Add product'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function ProductsSettings({ farmId }: { farmId: string }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ product: FarmProductResponse | null } | null>(null);

  // Separate key from qk.products.list: this list includes inactive products,
  // which the Applications picker must never see. Invalidating qk.products.list
  // still refreshes it (prefix match).
  const products = useQuery({
    queryKey: [...qk.products.list(farmId), 'manage'],
    queryFn: () =>
      api
        .get<{ data: FarmProductResponse[] }>(`/api/v1/farms/${farmId}/products`, {
          params: { sort: 'name', include_inactive: 'true' },
        })
        .then((r) => r.data ?? []),
  });

  const toggleActive = useMutation({
    mutationFn: (p: FarmProductResponse) =>
      api.patch(`/api/v1/farms/${farmId}/products/${p.id}`, {
        is_active: !p.is_active,
      } satisfies FarmProductUpdate),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.products.list(farmId) }),
  });

  return (
    <Card>
      <Card.Header
        action={
          <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setDialog({ product: null })}>
            Add product
          </Button>
        }
      >
        Products
      </Card.Header>
      <Card.Body>
        <p className="mb-4 text-sm text-gray-500">
          The list your team picks from when logging applications. Deactivate a product to hide it
          from the picker without losing past entries.
        </p>
        {products.isLoading ? (
          <LoadingSpinner />
        ) : products.error ? (
          <p className="text-sm font-medium text-status-critical">{products.error.message}</p>
        ) : products.data && products.data.length > 0 ? (
          <ul className="divide-y divide-gray-100">
            {products.data.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className={p.is_active ? 'break-words font-medium text-gray-900' : 'break-words font-medium text-gray-400'}>
                    {p.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {CATEGORY_LABEL[p.category] ?? p.category} · {p.unit}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={p.is_active ? 'success' : 'neutral'}>
                    {p.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button size="sm" variant="secondary" onClick={() => setDialog({ product: p })}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    loading={toggleActive.isPending && toggleActive.variables?.id === p.id}
                    onClick={() => toggleActive.mutate(p)}
                  >
                    {p.is_active ? 'Deactivate' : 'Reactivate'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No products yet. Add the first one.</p>
        )}
        {toggleActive.error && (
          <p className="mt-3 text-sm font-medium text-status-critical">{toggleActive.error.message}</p>
        )}
      </Card.Body>
      {dialog && (
        <ProductDialog farmId={farmId} product={dialog.product} onClose={() => setDialog(null)} />
      )}
    </Card>
  );
}
